import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Lock, Zap } from "lucide-react";
import { toast } from "sonner";

interface DemoStage {
  id: string;
  name: string;
  key: string;
  position: number;
  is_enabled: boolean;
  auto_run: boolean;
  automation_type: string;
  score_threshold: number;
}

const MOCK_STAGES: DemoStage[] = [
  {
    id: "1",
    name: "New Applicants",
    key: "new",
    position: 0,
    is_enabled: true,
    auto_run: true,
    automation_type: "none",
    score_threshold: 70,
  },
  {
    id: "2",
    name: "Shortlisted",
    key: "shortlisted",
    position: 1,
    is_enabled: true,
    auto_run: true,
    automation_type: "resume_match",
    score_threshold: 70,
  },
  {
    id: "3",
    name: "AI Interview",
    key: "ai_interview",
    position: 2,
    is_enabled: true,
    auto_run: true,
    automation_type: "send_ai_invite",
    score_threshold: 70,
  },
  {
    id: "4",
    name: "Ranked",
    key: "ranked",
    position: 3,
    is_enabled: true,
    auto_run: true,
    automation_type: "score_and_rank",
    score_threshold: 70,
  },
  {
    id: "5",
    name: "Face to Face",
    key: "face_to_face",
    position: 4,
    is_enabled: true,
    auto_run: false,
    automation_type: "none",
    score_threshold: 70,
  },
];

const automationLabels: Record<string, string> = {
  none: "",
  resume_match: "Auto Shortlist",
  send_ai_invite: "Send AI Interview",
  score_and_rank: "Score & Rank",
};

function DemoStageRow({
  stage,
  index,
  onUpdate,
}: {
  stage: DemoStage;
  index: number;
  onUpdate: (stageId: string, updates: Partial<DemoStage>) => void;
}) {
  const [name, setName] = useState(stage.name);
  const [threshold, setThreshold] = useState(stage.score_threshold);
  
  const isFirstStage = stage.key === "new";
  const hasAutomation = stage.automation_type !== "none";
  const isAutoShortlist = stage.automation_type === "resume_match";
  const isAiInterview = stage.automation_type === "send_ai_invite";

  const handleNameBlur = () => {
    if (name !== stage.name) {
      onUpdate(stage.id, { name });
      toast.success("Stage name updated (demo)");
    }
  };

  const handleThresholdBlur = () => {
    const validThreshold = Math.min(100, Math.max(1, threshold));
    if (validThreshold !== stage.score_threshold) {
      onUpdate(stage.id, { score_threshold: validThreshold });
      toast.success(`Threshold updated to ${validThreshold} (demo)`);
    }
  };

  return (
    <div className="p-3 sm:p-4 border rounded-lg bg-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium flex-shrink-0">
            {index + 1}
          </div>
          {isFirstStage ? (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium truncate">{stage.name}</span>
              <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </div>
          ) : (
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameBlur}
              className="h-8 w-full sm:w-32 font-medium"
            />
          )}
        </div>

        {!isFirstStage && (
          <div className="flex items-center gap-2 ml-8 sm:ml-0">
            <Label htmlFor={`demo-stage-${stage.id}`} className="text-xs sm:text-sm text-muted-foreground">
              {stage.is_enabled ? "Enabled" : "Disabled"}
            </Label>
            <Switch
              id={`demo-stage-${stage.id}`}
              checked={stage.is_enabled}
              onCheckedChange={(checked) => {
                onUpdate(stage.id, { is_enabled: checked });
                toast.success(`Stage ${checked ? "enabled" : "disabled"} (demo)`);
              }}
            />
          </div>
        )}
      </div>

      {isFirstStage && (
        <p className="text-sm text-muted-foreground">
          Intake stage for all new applications. Always enabled.
        </p>
      )}

      {hasAutomation && (
        <div className="mt-3 p-3 rounded bg-amber-50 dark:bg-amber-950/20 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Zap className="h-4 w-4 text-amber-600" />
              <span className="text-amber-700 dark:text-amber-400">
                {automationLabels[stage.automation_type]}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Label
                htmlFor={`demo-auto-${stage.id}`}
                className="text-xs text-muted-foreground"
              >
                Auto-run
              </Label>
              <Switch
                id={`demo-auto-${stage.id}`}
                checked={stage.auto_run}
                onCheckedChange={(checked) => {
                  onUpdate(stage.id, { auto_run: checked });
                  toast.success(`Auto-run ${checked ? "enabled" : "disabled"} (demo)`);
                }}
                disabled={!stage.is_enabled}
              />
            </div>
          </div>
          
          {/* Score threshold input for Auto Shortlist */}
          {isAutoShortlist && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t border-amber-200 dark:border-amber-800">
              <Label className="text-xs text-muted-foreground">
                Min resume score to shortlist
              </Label>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  value={threshold}
                  onChange={(e) => setThreshold(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
                  onBlur={handleThresholdBlur}
                  min={1}
                  max={100}
                  className="w-16 h-7 text-xs text-center"
                  disabled={!stage.is_enabled}
                />
                <span className="text-xs text-muted-foreground">/ 100</span>
              </div>
            </div>
          )}

          {/* Info message for AI Interview auto-run */}
          {isAiInterview && stage.auto_run && (
            <p className="text-xs text-amber-600 dark:text-amber-400 pt-2 border-t border-amber-200 dark:border-amber-800">
              ⚡ Candidates will automatically receive interview invitations when moved to this stage. 
              Make sure you have an interview configured for this job.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function DemoPipelineAutomation() {
  const [stages, setStages] = useState<DemoStage[]>(MOCK_STAGES);

  const handleUpdateStage = (stageId: string, updates: Partial<DemoStage>) => {
    setStages((prev) =>
      prev.map((stage) =>
        stage.id === stageId ? { ...stage, ...updates } : stage
      )
    );
  };

  const sortedStages = [...stages].sort((a, b) => a.position - b.position);

  return (
    <Card className="my-6 border-2 overflow-hidden max-w-full">
      <CardHeader className="pb-3 px-4 sm:px-6">
        <CardTitle className="text-base sm:text-lg">Pipeline Settings</CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          Configure hiring stages, toggle automations, and customize your pipeline.
        </CardDescription>
      </CardHeader>

      <CardContent className="px-3 sm:px-6">
        <ScrollArea className="max-h-[400px] sm:max-h-[500px]">
          <div className="space-y-2 sm:space-y-3 pr-1 sm:pr-2">
            {sortedStages.map((stage, index) => (
              <DemoStageRow
                key={stage.id}
                stage={stage}
                index={index}
                onUpdate={handleUpdateStage}
              />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
